import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import DialogLayout from '../../components/layout/DialogLayout';
import {
  Autocomplete,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  Pagination,
  Chip,
  Switch,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import Checkbox from '@mui/material/Checkbox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';

import {
  getOpportunity,
  uploadCorporateActionFile,
  getCorporateActionsInvestorList
} from '../../utils/api/api';
import { RECORD_DISPLAY_LIMIT, SNACKBAR_ERROR, SNACKBAR_SUCCESS } from '../../utils/constants/constants';
import { useSnackbar } from 'notistack';
import { BASE_URL, CLOSED_SCHEMES } from '../../utils/constants/constants';

/**
 * Corporate Action Dialog component for generating corporate action files
 * based on scheme and depository type selection
 */
const CorporateActionDialog = ({ open, toggleDialog }) => {
  // Scheme selection state
  const [schemeList, setSchemeList] = useState([]);
  const [selectedOppRids, setSelectedOppRids] = useState([]);
  const [dpType, setDpType] = useState('');

  const previousPageRef = useRef(1);
  const lastApiPayloadRef = useRef(null);

  // Investor list state
  const [investorList, setInvestorList] = useState([]);
  const [corpActionPage, setCorpActionPage] = useState(1);
  const [totalCorpActionRecords, setTotalCorpActionRecords] = useState(0);
  const [isInvestorListLoading, setIsInvestorListLoading] = useState(false);

  // Track deleted investor records
  const [deletedInvestorIds, setDeletedInvestorIds] = useState(new Set());

  // Toggle state for investors (NSDL escrow functionality)
  const [escrowToggles, setEscrowToggles] = useState({});

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const latestRequestIdRef = useRef(0);
  const isFetchingRef = useRef(false);

  const showToggles = dpType === 'NSDL';
  const showDeleteOption = true;

  const paginationCount = useMemo(() => {
    const activeRecordCount = totalCorpActionRecords - deletedInvestorIds.size;
    return Math.max(1, activeRecordCount > 0 ? Math.ceil(activeRecordCount / RECORD_DISPLAY_LIMIT) : 1);
  }, [totalCorpActionRecords, deletedInvestorIds]);

  const filteredInvestorList = useMemo(() => {
    return investorList.filter(investor => !deletedInvestorIds.has(investor.ioRid));
  }, [investorList, deletedInvestorIds]);

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '---';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const getStatusChip = (depType, isMoveToEscrow) => {
    if (!depType) return null;
    if (depType.toLowerCase() === 'escrow' || isMoveToEscrow === 1) {
      return <Chip
        label="ESCROW"
        size="small"
        sx={{
          backgroundColor: "#d32f2f",
          color: "white",
          borderColor: "#b71c1c",
          fontWeight: "medium",
          "& .MuiChip-label": {
            color: "white"
          }
        }}
      />;
    } else if (depType.toLowerCase() === 'nsdl') {
      return <Chip label="NSDL" size="small" color="primary" variant="outlined" />;
    } else if (depType.toLowerCase() === 'cdsl') {
      return <Chip label="CDSL" size="small" color="secondary" variant="outlined" />;
    }
    return <Chip label={depType} size="small" variant="outlined" />;
  };

  const downloadFile = (filePath, filename, contentType) => {
    return new Promise((resolve, reject) => {
      const fileUrl = `${BASE_URL}/${filePath}`;
      fetch(fileUrl)
        .then(response => {
          if (!response.ok) {
            throw new Error(`Failed to download file: ${response.status} ${response.statusText}`);
          }
          return response.blob();
        })
        .then(blob => {
          const fileBlob = new Blob([blob], { type: contentType });
          return downloadFileFromBlob(fileBlob, filename);
        })
        .then(() => {
          resolve(true);
        })
        .catch(err => {
          console.error('File download error:', err);
          enqueueSnackbar(`Error downloading ${filename}: ${err.message}`, SNACKBAR_ERROR);
          reject(err);
        });
    });
  };

  const downloadFileFromBlob = (blob, filename) => {
    return new Promise((resolve, reject) => {
      try {
        if (!blob || blob.size === 0) {
          console.error('Invalid or empty blob received');
          reject(new Error('Invalid file data received'));
          return;
        }
        const url = window.URL.createObjectURL(blob);
        const downloadLink = document.createElement('a');
        downloadLink.href = url;
        downloadLink.download = filename;
        downloadLink.click();
        window.URL.revokeObjectURL(url);
        resolve(true);
      } catch (error) {
        console.error('Download failed:', error);
        reject(error);
      }
    });
  };

  const fetchSchemeList = useCallback((param = {}) => {
    param.oppStatus = CLOSED_SCHEMES;
    setIsLoading(true);
    getOpportunity(param)
      .then((data) => {
        const filteredSchemes = (data.opportunityList || []).filter(
          scheme => scheme.oppStatusIndex === CLOSED_SCHEMES
        );
        setSchemeList(filteredSchemes);
      })
      .catch((error) => {
        enqueueSnackbar(error, SNACKBAR_ERROR);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [enqueueSnackbar]);

  const fetchInvestorList = useCallback(() => {
    if (isFetchingRef.current) return;
    const reqParams = {
    //  page: corpActionPage,
     // limit: RECORD_DISPLAY_LIMIT
    };
    if (selectedOppRids && selectedOppRids.length > 0) {
      if (selectedOppRids.length === 1) {
        reqParams.oppRid = selectedOppRids[0];
      } else {
        reqParams.oppRid = selectedOppRids.join(',');
      }
    }
    if (dpType) {
      reqParams.dpType = dpType;
    }

    const payloadSignature = JSON.stringify(reqParams);
    if (payloadSignature === lastApiPayloadRef.current) {
      return;
    }

    lastApiPayloadRef.current = payloadSignature;
    const requestId = Date.now();
    latestRequestIdRef.current = requestId;
    isFetchingRef.current = true;

    setIsInvestorListLoading(true);

    getCorporateActionsInvestorList(reqParams)
      .then((response) => {
        if (requestId !== latestRequestIdRef.current) {
          return;
        }
        if (response && response.investorList) {
          if (dpType === 'CDSL' && response && response.investorList) {
            const filteredList = response.investorList.filter(investor => investor.isMoveToEscrow !== 1);
            setInvestorList(filteredList);
            setTotalCorpActionRecords(response.records);
          } else {
            setInvestorList(response.investorList);
            setTotalCorpActionRecords(response.records || 0);
          }
          if (dpType === 'NSDL') {
            setEscrowToggles(prevToggles => {
              const newToggles = {};
              response.investorList.forEach(investor => {
                const id = investor.ioRid;
                if (id) {
                  const investorDpType = (investor.dpType || '').toLowerCase();
                  newToggles[id] = prevToggles[id] ||
                    investorDpType === 'cdsl' ||
                    investorDpType === 'escrow';
                }
              });
              return newToggles;
            });
          }
        } else {
          if (requestId === latestRequestIdRef.current) {
            setInvestorList([]);
            setTotalCorpActionRecords(0);
          }
        }
      })
      .catch((error) => {
        if (requestId !== latestRequestIdRef.current) return;
        console.error(`Error fetching investor list for page ${corpActionPage}:`, error);
        enqueueSnackbar(typeof error === 'string' ? error : "Failed to fetch investor list", SNACKBAR_ERROR);
      })
      .finally(() => {
        if (requestId === latestRequestIdRef.current) {
          setIsInvestorListLoading(false);
          isFetchingRef.current = false;
        }
      });
  }, [corpActionPage, dpType, selectedOppRids, enqueueSnackbar]);


  const handleToggleChange = useCallback((investorId, checked, investorDpType, isMoveToEscrow) => {
    if (investorDpType === 'cdsl' || investorDpType === 'escrow' || isMoveToEscrow === 1) return;
    setEscrowToggles(prev => ({
      ...prev,
      [investorId]: checked
    }));
  }, []);

  const handleDeleteInvestor = useCallback((investorId) => {
    if (!investorId) return;
    setDeletedInvestorIds(prev => {
      const newSet = new Set(prev);
      newSet.add(investorId);
      return newSet;
    });

    const remainingInPage = filteredInvestorList.length - 1;
    if (remainingInPage === 0 && corpActionPage > 1) {
      setCorpActionPage(prevPage => prevPage - 1);
    }

    enqueueSnackbar("Investor removed from list", {
      variant: "warning",
      autoHideDuration: RECORD_DISPLAY_LIMIT
    });
  }, [filteredInvestorList.length, corpActionPage, enqueueSnackbar]);

  const handleCorpActionPageChange = useCallback((event, value) => {
    previousPageRef.current = corpActionPage;
    isFetchingRef.current = false;
    setCorpActionPage(value);
  }, [corpActionPage]);

  React.useEffect(() => {
    if (open) {
      setSelectedOppRids([]);
      setDpType('');
      setError(null);
      setSchemeList([]);
      setInvestorList([]);
      setCorpActionPage(1);
      setTotalCorpActionRecords(0);
      setDeletedInvestorIds(new Set());
      setEscrowToggles({});
      latestRequestIdRef.current = 0;
      isFetchingRef.current = false;
      fetchSchemeList();

      const initialRequestId = Date.now();
      latestRequestIdRef.current = initialRequestId;
      isFetchingRef.current = true;

      setIsInvestorListLoading(true);
      getCorporateActionsInvestorList({
        page: 1,
        limit: RECORD_DISPLAY_LIMIT
      })
        .then((response) => {
          if (initialRequestId !== latestRequestIdRef.current) return;
          if (response && response.investorList) {
            setInvestorList(response.investorList);
            setTotalCorpActionRecords(response.records || 0);
            const toggles = {};
            response.investorList.forEach(investor => {
              if (investor.ioRid) {
                const investorDpType = (investor.dpType || '').toLowerCase();
                toggles[investor.ioRid] = investorDpType === 'cdsl' || investorDpType === 'escrow';
              }
            });
            setEscrowToggles(toggles);
          } else {
            setInvestorList([]);
            setTotalCorpActionRecords(0);
          }
        })
        .catch((error) => {
          if (initialRequestId !== latestRequestIdRef.current) return;

          console.error("Error fetching initial investor list:", error);
          enqueueSnackbar(typeof error === 'string' ? error : "Failed to fetch investor list", SNACKBAR_ERROR);
          setInvestorList([]);
          setTotalCorpActionRecords(0);
        })
        .finally(() => {
          if (initialRequestId === latestRequestIdRef.current) {
            setIsInvestorListLoading(false);
            isFetchingRef.current = false;
          }
        });
    }
    return () => {
      if (!open) {
        setSelectedOppRids([]);
        setDpType('');
      }
    };
  }, [open, fetchSchemeList, enqueueSnackbar]);

  React.useEffect(() => {
    if (!open) return;
    const isBackwardToPageOne = corpActionPage === 1 && previousPageRef.current > 1;
    if (dpType || (selectedOppRids && selectedOppRids.length > 0) || corpActionPage > 1 || isBackwardToPageOne) {
      const timeoutId = setTimeout(() => {
        fetchInvestorList();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [open, dpType, selectedOppRids, corpActionPage, fetchInvestorList]);



  const prepareApiPayload = useCallback(() => {
    const getInvestorId = investor => investor.ioRid;
    const activeInvestorList = filteredInvestorList;

    if ((!selectedOppRids || selectedOppRids.length === 0) && !dpType) {
      const allInvestorIds = activeInvestorList
        .map(getInvestorId)
        .filter(Boolean);
      if (allInvestorIds.length === 0) return null;
      return {
        dpType: "ALL",
        ioRids: allInvestorIds
      };
    }

    const payload = {
      dpType: dpType || "",
      deletedIoRids: [...deletedInvestorIds],
    };

    if (selectedOppRids && selectedOppRids.length > 0) {
      payload.oppRids = selectedOppRids.join(',');
    }

    if (dpType) {
      if (dpType === 'CDSL') {
        const cdslInvestors = activeInvestorList.filter(investor =>
          (investor.dpType || '').toLowerCase() === 'cdsl' && investor.isMoveToEscrow !== 1);


        const cdslIoRids = cdslInvestors
          .map(getInvestorId)
          .filter(Boolean);
        if (cdslIoRids.length === 0 && (!selectedOppRids || selectedOppRids.length === 0)) return null;
        if (cdslIoRids.length > 0) {
          payload.ioRids = cdslIoRids;
        }
      } else if (dpType === 'NSDL') {
        const regularInvestors = [];
        const escrowInvestors = [];

        activeInvestorList.forEach(investor => {
          const id = getInvestorId(investor);
          if (!id) return;

          const investorDpType = (investor.dpType || '').toLowerCase();
          const isEscrowType = investorDpType === 'escrow';
          const isToggledToEscrow = escrowToggles[id] === true;
          const isFlaggedForEscrow = investor.isMoveToEscrow === 1;

          if (isEscrowType || isToggledToEscrow || isFlaggedForEscrow) {
            escrowInvestors.push(investor);
          } else {
            regularInvestors.push(investor);
          }
        });

        const regularIoRids = regularInvestors
          .map(getInvestorId)
          .filter(Boolean);

        const escrowIoRids = escrowInvestors
          .map(getInvestorId)
          .filter(Boolean);

        if (regularIoRids.length === 0 && escrowIoRids.length === 0 &&
          (!selectedOppRids || selectedOppRids.length === 0)) return null;

        if (regularIoRids.length > 0) {
          payload.ioRids = regularIoRids;
        }

        if (escrowIoRids.length > 0) {
          payload.escrowRids = escrowIoRids;
        }
      }
    } else if (selectedOppRids && selectedOppRids.length > 0) {
      const allInvestorIds = activeInvestorList
        .map(getInvestorId)
        .filter(Boolean);

      if (allInvestorIds.length > 0) {
        payload.ioRids = allInvestorIds;
      } else {
        return null;
      }
    }

    return payload;
  }, [dpType, filteredInvestorList, escrowToggles, selectedOppRids]);
  const handleGenerate = useCallback(async () => {
    let successCount = 0;

    if (filteredInvestorList.length === 0) {
      enqueueSnackbar("No investors available. All records have been removed.", { variant: "warning" });
      return;
    }

    if (!dpType) {
      enqueueSnackbar("Please select a DP Type", { variant: "warning" });
      return;
    }

    const payload = prepareApiPayload();

    if (!payload) {
      enqueueSnackbar("No eligible investors found for selected criteria", { variant: "warning" });
      return;
    }

    setIsGenerating(true);

    try {
      const response = await uploadCorporateActionFile(payload);

      let fileData = [];
      if (response?.data?.data && Array.isArray(response.data.data)) {
        fileData = response.data.data;
      } else if (response?.data && Array.isArray(response.data)) {
        fileData = response.data;
      } else if (Array.isArray(response)) {
        fileData = response;
      }

      if (!fileData.length) {
        console.error('No file data found in response:', response);
        enqueueSnackbar("No files generated", { variant: "error" });
        setIsGenerating(false);
        return;
      }

      for (const file of fileData) {
        if (!file.path) {
          console.error('File path missing:', file);
          continue;
        }
        try {
          await downloadFile(
            file.path,
            file.actualFileName || `corporate_action_${Date.now()}.${file.path.split('.').pop()}`,
            file.contentType || 'application/octet-stream'
          );
          successCount++;
          await new Promise(resolve => setTimeout(resolve, 1000));
        } catch (error) {
          console.error(`Error downloading file ${file.actualFileName || 'unknown'}:`, error);
        }
      }

      if (successCount > 0) {
        enqueueSnackbar(`Files downloaded successfully`, { variant: "success" });
        handleClose(true);
      } else {
        enqueueSnackbar("No files were downloaded successfully", { variant: "error" });
      }
    } catch (error) {
      console.error("Error in corporate action process:", error);
      enqueueSnackbar(typeof error === 'string' ? error : "Failed to process corporate action files", SNACKBAR_ERROR);
    } finally {
      setIsGenerating(false);
    }
  }, [dpType, prepareApiPayload, filteredInvestorList.length, enqueueSnackbar]);

  const handleSchemeChange = useCallback((event, values) => {
    const oppRids = values.map(value => value.oppRid);
    setSelectedOppRids(oppRids);
    setCorpActionPage(1);
  }, []);

  const handleDpTypeChange = useCallback((event) => {
    setDpType(event.target.value);
    setCorpActionPage(1);
  }, []);

  const handleClose = (dataAdded = false) => {
    setSelectedOppRids([]);
    setDpType('');
    toggleDialog(dataAdded);
  };

  const renderInvestorTable = () => (
    <div className="overflow-x-scroll">
      <Box sx={{ position: 'relative' }}>
        <table className="table table-striped table-borderless w-100 position-relative" id="order-table">
          <thead>
            <tr className="fs-normal-semibold text-center sticky-table-row" style={{ backgroundColor: "#f5f5f5" }}>
              <th scope="col" style={{ minWidth: "240px" }}>
                <p className="m-0">Investor Name</p>
              </th>
              <th scope="col" style={{ minWidth: "120px", textAlign: "right", paddingRight: "20px" }}>
                <p className="m-0">Commitment</p>
              </th>
              <th scope="col" style={{ minWidth: "120px", textAlign: "left", paddingLeft: "20px" }}>
                <p className="m-0">Scheme</p>
              </th>
              <th scope="col" style={{ minWidth: "120px", textAlign: "center" }}>
                <p className="m-0">DP ID</p>
              </th>
              <th scope="col" style={{ minWidth: "120px", textAlign: "center" }}>
                <p className="m-0">Client ID</p>
              </th>
              <th scope="col" style={{ minWidth: "100px", textAlign: "center" }}>
                <p className="m-0">DP Type</p>
              </th>
              {/* Only show Escrow column when NSDL is selected */}
              {showToggles && (
                <th scope="col" style={{ minWidth: "60px", textAlign: "center" }}>
                  <p className="m-0 text-center">Escrow</p>
                </th>
              )}
              {showDeleteOption && (
                <th scope="col" style={{ minWidth: "60px", textAlign: "center" }}>
                  <p className="m-0 text-center">Delete</p>
                </th>
              )}
            </tr>
          </thead>
          <tbody className="bg-table-blue">
            {isInvestorListLoading && filteredInvestorList.length === 0 ? (
              <tr>
                <td colSpan={6 + (showToggles ? 1 : 0) + (showDeleteOption ? 1 : 0)} className="text-center">
                  <CircularProgress size={24} />
                </td>
              </tr>
            ) : filteredInvestorList.length > 0 ? (
              filteredInvestorList.map((investor, index) => {
                const investorId = investor.ioRid;
                const investorDpType = (investor.dpType || '').toLowerCase();

                return (
                  <tr key={`${investorId || index}-${index}`} className="fs-normal-medium text-center">
                    <td className="align-middle">
                      <div style={{ textAlign: "left" }}>
                        <span style={{ display: "block" }}>{investor.fullName}</span>
                      </div>
                    </td>
                    <td className="align-middle" style={{ textAlign: "right", paddingRight: "20px" }}>
                      {formatCurrency(investor.commitment)}
                    </td>
                    <td className="align-middle" style={{ textAlign: "left", paddingLeft: "20px" }}>
                      {investor.oppTitle}
                    </td>
                    <td className="align-middle text-center">{investor.dpId || '-'}</td>
                    <td className="align-middle text-center">{investor.dpClientId || '-'}</td>
                    <td className="align-middle text-center">
                      {getStatusChip(investor.dpType, investor.isMoveToEscrow)}
                    </td>

                    {/* Escrow toggle with proper centering - show for ALL investors when NSDL is selected */}
                    {showToggles && (
                      <td className="align-middle" style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                          <Switch
                            size="small"
                            checked={escrowToggles[investorId] || investorDpType === 'escrow' || investorDpType === 'cdsl' || investor.isMoveToEscrow === 1}
                            onChange={(e) => handleToggleChange(investorId, e.target.checked, investorDpType)}
                            disabled={investorDpType === 'escrow' || investorDpType === 'cdsl' || investor.isMoveToEscrow === 1}
                          />
                        </div>
                      </td>
                    )}

                    {/* Delete button with proper centering */}
                    {showDeleteOption && (
                      <td className="align-middle" style={{ textAlign: "center" }}>
                        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteInvestor(investorId)}
                            aria-label="delete investor"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6 + (showToggles ? 1 : 0) + (showDeleteOption ? 1 : 0)}>
                  <p className="text-center">No Records Found</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {/* Add overlay when loading but data exists */}
        {isInvestorListLoading && filteredInvestorList.length > 0 && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255,255,255,0.6)',
              zIndex: 5
            }}
          >
            <CircularProgress size={40} />
          </Box>
        )}
      </Box>
    </div>
  );

  const dialogContent = (
    <>
      {isLoading && !filteredInvestorList.length ? (
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <CircularProgress />
        </div>
      ) : (
        <div>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container rowSpacing={3} columnSpacing={{ xs: 1, sm: 1, md: 1 }} className='py-2'>
            <Grid item xs={12} md={6}>
              <Autocomplete
                id="scheme-select"
                multiple
                disableCloseOnSelect
                options={schemeList}
                getOptionLabel={(option) => option.oppTitle}
                value={schemeList.filter(option => selectedOppRids.includes(option.oppRid))}
                onChange={handleSchemeChange}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option.oppRid}>
                    <Checkbox
                      icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                      checkedIcon={<CheckBoxIcon fontSize="small" />}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.oppTitle}
                  </li>
                )}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    position: 'relative',
                    overflow: 'hidden',
                    paddingRight: '72px',
                    minHeight: '56px',
                    height: '56px',
                    '& fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.23)',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(0, 0, 0, 0.87)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiAutocomplete-inputRoot': {
                    display: 'flex',
                    alignItems: 'center',
                    height: '100%',
                    padding: '0',
                    '& .MuiAutocomplete-input': {
                      width: 'auto',
                      flex: '1 1 auto',
                      minWidth: '30px',
                      padding: '8px 12px',
                      paddingRight: selectedOppRids.length > 0 ? '100px' : '12px',
                    },
                  },
                  '& .MuiAutocomplete-endAdornment': {
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 3,
                    backgroundColor: 'transparent',
                    '& .MuiAutocomplete-clearIndicator': {
                      visibility: 'visible',
                      padding: '4px',
                    },
                    '& .MuiAutocomplete-popupIndicator': {
                      padding: '4px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -9px) scale(0.75)',
                    },
                  },
                }}
                renderInput={(params) => (
                  <Box sx={{ position: 'relative' }}>
                    <TextField
                      {...params}
                      label="Select Schemes"
                      placeholder={selectedOppRids.length === 0 ? "Select one or more schemes" : ""}
                      fullWidth
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                      }}
                      InputLabelProps={{
                        ...params.InputLabelProps,
                        shrink: true,
                      }}
                    />

                    {selectedOppRids.length > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          right: '80px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#000',
                          fontSize: '1rem',
                          fontWeight: 460,
                          backgroundColor: 'transparent',
                          padding: '0 4px',
                          zIndex: 2,
                          pointerEvents: 'none',
                        }}
                      >
                        {selectedOppRids.length} Selected
                      </Box>
                    )}
                  </Box>
                )}
                renderTags={() => null}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="depository-type-label">DP Type</InputLabel>
                <Select
                  labelId="depository-type-label"
                  id="depository-type"
                  value={dpType}
                  label="DP Type"
                  onChange={handleDpTypeChange}
                >
                  <MenuItem value="NSDL">NSDL</MenuItem>
                  <MenuItem value="CDSL">CDSL</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} style={{ paddingBottom: 10, paddingTop: 10 }}>
              <Divider />
            </Grid>
          </Grid>

          <Box sx={{ mt: 2 }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2
            }}>
              <Box>
                {deletedInvestorIds.size > 0 && (
                  <Chip
                    label={`${deletedInvestorIds.size} record(s) removed`}
                    color="default"
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>

              {/* Right side: Pagination */}
              {/* <Box>
                {totalCorpActionRecords > 0 && (
                  <Pagination
                    color="secondary"
                    size="small"
                    siblingCount={0}
                    onChange={handleCorpActionPageChange}
                    count={paginationCount}
                    page={corpActionPage}
                  />
                )}
              </Box> */}
            </Box>

            {/* Table container */}
            <Box sx={{ position: 'relative' }}>
              {renderInvestorTable()}
            </Box>
          </Box>

          {isGenerating && (
            <Box
              sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255,255,255,0.7)',
                zIndex: 1000
              }}
            >
              <CircularProgress />
            </Box>
          )}
        </div>
      )}
    </>
  );

  return (
    <DialogLayout
      key={`corporate-action-dialog-${open ? 'open' : 'closed'}`}
      open={open}
      maxWidth="lg"
      title="Corporate Action"
      closeAction={toggleDialog}
      positiveAction={{
        label: "Generate",
        onClick: handleGenerate,
        disabled: (!dpType) || filteredInvestorList.length === 0 || isLoading || isGenerating
      }}
      negativeAction={{
        label: "Cancel",
        onClick: handleClose,
        disabled: isGenerating
      }}
    >
      {dialogContent}
    </DialogLayout>
  );
};

// PropTypes
CorporateActionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  toggleDialog: PropTypes.func.isRequired
};

export default CorporateActionDialog;